/**
 * Matrix Pricing Engine
 *
 * Calculates product prices based on multi-dimensional matrices.
 * Supports tier rounding, unavailable combinations, and complex pricing rules.
 *
 * Example:
 * Product with Breedte (200-500) × Uitval (150-300)
 * Matrix defines price for each combination
 * Tier rounding: 2600mm → 3000mm tier
 *
 * KEEP IN SYNC with smartflow-api/services/productPricingService.js
 * (calculateProductMatrixPrice): the backend prices dashboard sales orders and
 * proposal lines with its own copy of this matrix walk and tier rounding.
 */

import { resolveChoiceAxisValue } from '../utils/choiceAxis.js';

export class MatrixPricingEngine {
  /**
   * Resolve the matching type for a matrix dimension based on the underlying
   * product option. Number options use numeric (tolerant) matching; all other
   * types (single/multi-choice, toggle) match on their string label so values
   * like "Ja"/"Nee" compare correctly.
   *
   * @param {Object} product - Product with options
   * @param {string} optionId - The dimension's option id
   * @returns {'number'|'choice'}
   */
  resolveDimensionType(product, optionId) {
    const option = product?.options?.find((o) => o.id === optionId);
    if (option?.type === 'number') return 'number';

    const computedFields = product?.computedFields || product?.computed_fields || [];
    if (Array.isArray(computedFields) && computedFields.some((cf) => cf?.id === optionId)) {
      return 'number';
    }

    return 'choice';
  }

  /**
   * Resolve a matrix dimension value from configuration.
   * Prefers the option input field, then falls back to a computed-field id.
   */
  resolveDimensionValue(product, optionId, configuration = {}) {
    const fieldId = `${product.id}-${optionId}-input`;
    const fromInput = configuration[fieldId];
    if (fromInput !== undefined && fromInput !== null && fromInput !== '') {
      return fromInput;
    }

    const fromComputed = configuration[optionId];
    if (fromComputed !== undefined && fromComputed !== null && fromComputed !== '') {
      return fromComputed;
    }

    return undefined;
  }

  normalizeDimensions(dimensions, product = {}) {
    if (Array.isArray(dimensions)) {
      return dimensions.map((dimension) => ({
        ...dimension,
        label:
          dimension.label ||
          this.resolveDimensionLabel(product, dimension.optionId, dimension.label || 'Dimension'),
        type: dimension.type || this.resolveDimensionType(product, dimension.optionId),
      }));
    }

    if (dimensions && typeof dimensions === 'object') {
      const result = [];
      if (dimensions.row) {
        result.push({
          optionId: dimensions.row,
          label: this.resolveDimensionLabel(product, dimensions.row, 'Row'),
          type: this.resolveDimensionType(product, dimensions.row),
        });
      }
      if (dimensions.col) {
        result.push({
          optionId: dimensions.col,
          label: this.resolveDimensionLabel(product, dimensions.col, 'Column'),
          type: this.resolveDimensionType(product, dimensions.col),
        });
      }
      if (dimensions.group) {
        result.push({
          optionId: dimensions.group,
          label: this.resolveDimensionLabel(product, dimensions.group, 'Group'),
          type: this.resolveDimensionType(product, dimensions.group),
        });
      }
      return result;
    }

    return [];
  }

  resolveDimensionLabel(product, optionId, fallback = 'Dimension') {
    const option = product?.options?.find((o) => o.id === optionId);
    if (option?.label) return option.label;

    const computedFields = product?.computedFields || product?.computed_fields || [];
    const computed = computedFields.find((field) => field?.id === optionId);
    if (computed?.label) return computed.label;

    return fallback;
  }

  /**
   * Calculate cumulative delta from optional lookup delta tables.
   *
   * @param {Object} product - Product with optional pricing_deltas
   * @param {Object} configuration - User selections (fieldId: value)
   * @returns {Object} { totalDelta, items }
   */
  calculateDeltaTables(product, configuration) {
    const tables = product?.pricing_deltas;
    if (!Array.isArray(tables) || tables.length === 0) {
      return {
        totalDelta: 0,
        items: [],
      };
    }

    const items = [];
    let totalDelta = 0;

    tables
      .filter((table) => table && table.active !== false)
      .forEach((table, tableIndex) => {
        const normalized = this.normalizeDeltaTable(table, product);
        if (!normalized || !normalized.dimensions?.length) {
          return;
        }

        const dimensionValues = {};
        const tierAdjustments = [];

        for (const dimension of normalized.dimensions) {
          let value = this.resolveDimensionValue(product, dimension.optionId, configuration);

          if (value === undefined || value === null || value === '') {
            value = undefined;
            break;
          }

          value = this.resolveAxisValue(product, dimension.optionId, value, normalized);

          const isNumeric = this.resolveDimensionType(product, dimension.optionId) === 'number';
          const autoTiers = isNumeric
            ? this.extractTiersFromCells({ cells: normalized.cells || [] }, dimension.optionId)
            : [];

          if (autoTiers.length > 0) {
            const originalValue = value;
            value = this.applyTierRounding(value, autoTiers);
            if (String(originalValue) !== String(value)) {
              tierAdjustments.push({
                dimension: dimension.label,
                original: originalValue,
                rounded: value,
              });
            }
          }

          dimensionValues[dimension.optionId] = value;
        }

        const hasAllDimensions = normalized.dimensions.every(
          (dim) => dimensionValues[dim.optionId] !== undefined
        );

        const fallbackValue = Number(table.defaultValue || 0);
        let deltaValue = fallbackValue;
        let matched = false;
        let matchSource = 'default';

        if (hasAllDimensions) {
          const matchedCell = this.findMatrixCell(normalized, dimensionValues);
          if (matchedCell && matchedCell.available !== false) {
            const raw = matchedCell.value ?? matchedCell.price ?? 0;
            deltaValue = Number(raw) || 0;
            matched = true;
            matchSource = 'cell';
          }
        }

        totalDelta += deltaValue;
        items.push({
          id: table.id || `delta_table_${tableIndex + 1}`,
          label: table.label || `Delta tabel ${tableIndex + 1}`,
          value: deltaValue,
          matched,
          matchSource,
          defaultValue: fallbackValue,
          dimensionValues,
          tierAdjustments,
        });
      });

    return {
      totalDelta,
      items,
    };
  }

  /**
   * Calculate price from matrix based on user configuration
   *
   * @param {Object} product - Product with pricingMatrix config
   * @param {Object} configuration - User's selected values (fieldId: value)
   * @returns {Object} { available, price, tierAdjustments, error, cell }
   */
  calculatePrice(product, configuration) {
    if (!product.useMatrixPricing || !product.pricingMatrix) {
      return {
        available: false,
        error: 'Matrix pricing not configured for this product',
      };
    }

    // Convert grid structure to cells structure if needed
    const matrix = this.convertGridToCells(product.pricingMatrix, product);
    const dimensions = this.normalizeDimensions(matrix?.dimensions, product);
    if (dimensions.length === 0) {
      return {
        available: false,
        error: 'Matrix has no valid dimensions configured',
      };
    }

    // 1. Extract dimension values from configuration
    const dimensionValues = {};
    const tierAdjustments = [];

    for (const dimension of dimensions) {
      let value = this.resolveDimensionValue(product, dimension.optionId, configuration);

      if (value === undefined || value === null || value === '') {
        return {
          available: false,
          error: `Missing value for dimension: ${dimension.label}`,
        };
      }

      value = this.resolveAxisValue(product, dimension.optionId, value, matrix);

      // Apply tier rounding based on matrix values. Choice labels like "100cm | 90°"
      // must match exactly, so only numeric dimensions are rounded.
      const autoTiers =
        dimension.type === 'number' ? this.extractTiersFromCells(matrix, dimension.optionId) : [];

      if (autoTiers.length > 0) {
        const originalValue = value;
        value = this.applyTierRounding(value, autoTiers);

        if (String(originalValue) !== String(value)) {
          tierAdjustments.push({
            dimension: dimension.label,
            original: originalValue,
            rounded: value,
            tierLabel: String(value),
          });
        }
      } else if (dimension.tierRounding && dimension.tiers && dimension.tiers.length > 0) {
        const originalValue = value;
        value = this.applyTierRounding(value, dimension.tiers);

        if (originalValue !== value) {
          tierAdjustments.push({
            dimension: dimension.label,
            original: originalValue,
            rounded: value,
            tierLabel: dimension.tiers.find((t) => t.value === value)?.label || value,
          });
        }
      }

      dimensionValues[dimension.optionId] = value;
    }

    // 2. Find matching cell in matrix
    const cell = this.findMatrixCell(matrix, dimensionValues);

    if (!cell) {
      return {
        available: false,
        error: 'This combination is not available in the price matrix',
        dimensionValues,
      };
    }

    if (!cell.available) {
      return {
        available: false,
        error: 'This combination is marked as unavailable',
        dimensionValues,
      };
    }

    // 3. Return successful result
    return {
      available: true,
      price: cell.price,
      tierAdjustments,
      dimensionValues,
      cell,
    };
  }

  /**
   * Resolve a configured value to the value the matrix or delta axis is keyed on.
   * Choices resolve to their id, priceKey or label (whichever the axis holds);
   * toggles to a boolean; numbers pass through.
   *
   * @param {Object} product - Product object with options
   * @param {string} optionId - The option ID
   * @param {*} value - The value (could be a choice ID or raw value)
   * @param {Object} table - Matrix or delta table in cells format
   * @returns {*} The value to use for cell comparison
   */
  resolveAxisValue(product, optionId, value, table = null) {
    if (!product?.options) return value;

    const option = product.options.find((o) => o.id === optionId);
    if (!option) return value;

    // For toggle options the matrix stores stable booleans (true/false), not the
    // display labels. Normalize the value to a boolean for cell matching.
    if (option.type === 'toggle') {
      return value === true || String(value).trim().toLowerCase() === 'true';
    }

    const axisValues = (table?.cells || []).map((cell) => cell?.dimensions?.[optionId]);
    return resolveChoiceAxisValue(option, value, axisValues);
  }

  /**
   * Extract unique tier values from matrix cells for a dimension
   * Used when tier rounding is not explicitly configured
   *
   * @param {Object} matrix - Matrix with cells
   * @param {string} dimensionId - Which dimension to extract tiers for
   * @returns {Array} Array of tier objects: [{ value: 1000 }, { value: 1100 }, ...]
   */
  extractTiersFromCells(matrix, dimensionId) {
    if (!matrix.cells || !Array.isArray(matrix.cells)) {
      return [];
    }

    const uniqueValues = new Set();
    matrix.cells.forEach((cell) => {
      const value = cell.dimensions[dimensionId];
      if (value !== undefined && value !== null) {
        // Parse to number if possible for proper sorting
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          uniqueValues.add(numValue);
        } else {
          uniqueValues.add(value);
        }
      }
    });

    // Convert to tier objects and sort
    return Array.from(uniqueValues)
      .sort((a, b) => {
        const aNum = parseFloat(a);
        const bNum = parseFloat(b);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        return String(a).localeCompare(String(b));
      })
      .map((v) => ({ value: v }));
  }

  /**
   * Apply tier rounding to a value
   * Example: 2600 with tiers [1000, 2000, 3000] → 3000
   *
   * @param {number|string} value - User input value
   * @param {Array} tiers - Array of tier objects: [{ value: 1000, label: "1m" }]
   * @returns {number} Rounded value
   */
  applyTierRounding(value, tiers) {
    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return value; // Can't round non-numeric values
    }

    // Sort tiers by value ascending
    const sortedTiers = [...tiers].sort((a, b) => a.value - b.value);

    // Find the smallest tier that is >= user value
    for (const tier of sortedTiers) {
      if (numValue <= tier.value) {
        return tier.value;
      }
    }

    // If value exceeds all tiers, use the highest tier
    return sortedTiers[sortedTiers.length - 1].value;
  }

  /**
   * Find cell in matrix that matches all dimension values
   *
   * @param {Object} matrix - Pricing matrix configuration
   * @param {Object} dimensionValues - { optionId: value } mapping
   * @returns {Object|null} Matching cell or null
   */
  findMatrixCell(matrix, dimensionValues) {
    if (!matrix.cells || matrix.cells.length === 0) {
      return null;
    }

    const dimensions = this.normalizeDimensions(matrix?.dimensions);
    if (dimensions.length === 0) {
      return null;
    }

    return matrix.cells.find((cell) => {
      // Check if all dimensions match
      return dimensions.every((dim) => {
        const cellValue = cell.dimensions[dim.optionId];
        const userValue = dimensionValues[dim.optionId];

        return this.valuesMatch(cellValue, userValue, dim);
      });
    });
  }

  /**
   * Check if two values match (with tolerance for numbers)
   *
   * @param {*} cellValue - Value from matrix cell
   * @param {*} userValue - Value from user configuration
   * @param {Object} dimension - Dimension configuration
   * @returns {boolean} True if values match
   */
  valuesMatch(cellValue, userValue, dimension) {
    // For number dimensions, allow small floating point differences
    if (dimension.type === 'number') {
      const cellNum = parseFloat(cellValue);
      const userNum = parseFloat(userValue);

      if (isNaN(cellNum) || isNaN(userNum)) {
        return false;
      }

      return Math.abs(cellNum - userNum) < 0.01;
    }

    // For other types, match case-insensitively and ignore surrounding whitespace
    // so e.g. "TRUE" matches true and "Motorsteun " matches "motorsteun".
    return (
      cellValue === userValue ||
      this.normalizeComparisonValue(cellValue) === this.normalizeComparisonValue(userValue)
    );
  }

  /**
   * Normalize a value for string comparison: trim whitespace and lowercase.
   *
   * @param {*} value - Value to normalize
   * @returns {string} Normalized string
   */
  normalizeComparisonValue(value) {
    return String(value).trim().toLowerCase();
  }

  /**
   * Get available values for a specific dimension
   * Useful for dynamically filtering options based on other selections
   *
   * @param {Object} matrix - Pricing matrix
   * @param {string} dimensionId - Which dimension to get values for
   * @param {Object} otherDimensionValues - Already selected values for other dimensions
   * @returns {Array} Available values for this dimension
   */
  getAvailableValuesForDimension(matrix, dimensionId, otherDimensionValues = {}) {
    if (!matrix.cells || !Array.isArray(matrix.cells)) {
      return [];
    }

    const availableValues = new Set();
    let matchCount = 0;

    matrix.cells
      .filter((cell) => cell.available !== false)
      .forEach((cell, idx) => {
        // Check if this cell matches the other dimension values
        // If no other dimension values are provided, include all cells
        const hasOtherValues = Object.keys(otherDimensionValues).length > 0;
        const matchesOthers =
          !hasOtherValues ||
          Object.entries(otherDimensionValues).every(([dimId, value]) => {
            if (dimId === dimensionId) return true; // Skip the dimension we're checking
            const cellValue = cell.dimensions[dimId];
            // Compare as normalized strings to handle number/string mismatches,
            // casing differences, and surrounding whitespace
            const matches =
              this.normalizeComparisonValue(cellValue) === this.normalizeComparisonValue(value);
            return matches;
          });

        if (matchesOthers && cell.dimensions[dimensionId] !== undefined) {
          availableValues.add(cell.dimensions[dimensionId]);
          matchCount++;
        }
      });

    const result = Array.from(availableValues).sort((a, b) => {
      // Sort numerically if both are numbers
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      // Otherwise sort alphabetically
      return String(a).localeCompare(String(b));
    });

    return result;
  }

  /**
   * Validate matrix configuration
   * Check for missing cells, duplicate cells, invalid data
   *
   * @param {Object} matrix - Pricing matrix to validate
   * @returns {Object} { valid, errors, warnings }
   */
  validateMatrix(matrix) {
    const errors = [];
    const warnings = [];
    const dimensions = this.normalizeDimensions(matrix?.dimensions);

    // Check dimensions
    if (dimensions.length === 0) {
      errors.push('Matrix must have at least one dimension');
    }

    if (!matrix.cells || matrix.cells.length === 0) {
      errors.push('Matrix must have at least one price cell');
    }

    // Check for duplicate cells
    const cellKeys = new Set();
    matrix.cells?.forEach((cell, index) => {
      const key = dimensions
        .map((dim) => `${dim.optionId}:${cell.dimensions[dim.optionId]}`)
        .join('|');

      if (cellKeys.has(key)) {
        errors.push(`Duplicate cell found at index ${index}: ${key}`);
      }
      cellKeys.add(key);
    });

    // Check for incomplete cells
    matrix.cells?.forEach((cell, index) => {
      dimensions.forEach((dim) => {
        if (cell.dimensions[dim.optionId] === undefined) {
          warnings.push(`Cell ${index} missing value for dimension: ${dim.label}`);
        }
      });

      if (cell.price === undefined || cell.price === null) {
        warnings.push(`Cell ${index} has no price set`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Generate all possible combinations for matrix dimensions
   * Useful for pre-filling matrix editor
   *
   * @param {Array} dimensions - Array of dimension configs
   * @returns {Array} All possible combinations
   */
  generateAllCombinations(dimensions) {
    if (dimensions.length === 0) return [];
    if (dimensions.length === 1) {
      const dim = dimensions[0];
      const values = this.getDimensionPossibleValues(dim);
      return values.map((v) => ({ [dim.optionId]: v }));
    }

    // Recursive combination generation
    const [firstDim, ...restDims] = dimensions;
    const firstValues = this.getDimensionPossibleValues(firstDim);
    const restCombinations = this.generateAllCombinations(restDims);

    const allCombinations = [];
    for (const value of firstValues) {
      for (const restCombo of restCombinations) {
        allCombinations.push({
          [firstDim.optionId]: value,
          ...restCombo,
        });
      }
    }

    return allCombinations;
  }

  /**
   * Get possible values for a dimension
   *
   * @param {Object} dimension - Dimension configuration
   * @returns {Array} Possible values
   */
  getDimensionPossibleValues(dimension) {
    if (dimension.tiers && dimension.tiers.length > 0) {
      return dimension.tiers.map((t) => t.value);
    }

    // If no tiers defined, return empty (manual entry required)
    return [];
  }

  /**
   * Convert grid structure to cells structure
   * Converts the editor's grid format to the engine's cells format
   *
   * @param {Object} pricingMatrix - Pricing matrix with grid structure
   * @param {Object} product - Product object (for option labels)
   * @returns {Object} Converted matrix with cells and dimensions array
   */
  convertGridToCells(pricingMatrix, product = {}) {
    if (!pricingMatrix || typeof pricingMatrix !== 'object') {
      return pricingMatrix;
    }

    // If already in cells format, return as-is
    if (pricingMatrix.cells && Array.isArray(pricingMatrix.cells)) {
      // Check if dimensions is already an array
      if (Array.isArray(pricingMatrix.dimensions)) {
        return pricingMatrix;
      }
      // If dimensions is object, convert to array
      if (pricingMatrix.dimensions && typeof pricingMatrix.dimensions === 'object') {
        const dimensionsArray = [];
        if (pricingMatrix.dimensions.row) {
          const rowOption = product.options?.find((o) => o.id === pricingMatrix.dimensions.row);
          dimensionsArray.push({
            optionId: pricingMatrix.dimensions.row,
            label: rowOption?.label || 'Row',
            type: this.resolveDimensionType(product, pricingMatrix.dimensions.row),
          });
        }
        if (pricingMatrix.dimensions.col) {
          const colOption = product.options?.find((o) => o.id === pricingMatrix.dimensions.col);
          dimensionsArray.push({
            optionId: pricingMatrix.dimensions.col,
            label: colOption?.label || 'Column',
            type: this.resolveDimensionType(product, pricingMatrix.dimensions.col),
          });
        }
        if (pricingMatrix.dimensions.group) {
          const groupOption = product.options?.find((o) => o.id === pricingMatrix.dimensions.group);
          dimensionsArray.push({
            optionId: pricingMatrix.dimensions.group,
            label: groupOption?.label || 'Group',
            type: this.resolveDimensionType(product, pricingMatrix.dimensions.group),
          });
        }
        return {
          ...pricingMatrix,
          dimensions: dimensionsArray,
        };
      }
      return pricingMatrix;
    }

    // Convert grid structure to cells
    const grid = Array.isArray(pricingMatrix.grid) ? pricingMatrix.grid : [];
    const dimensions = pricingMatrix.dimensions;
    const columnValues = Array.isArray(pricingMatrix.columnValues)
      ? pricingMatrix.columnValues
      : Array.isArray(pricingMatrix.column_values)
        ? pricingMatrix.column_values
        : [];
    if (!grid || !dimensions || !columnValues) {
      return pricingMatrix; // Return as-is if structure is incomplete
    }

    const cells = [];
    const dimensionsArray = [];

    // Convert dimensions object to array
    if (dimensions.row) {
      const rowOption = product.options?.find((o) => o.id === dimensions.row);
      dimensionsArray.push({
        optionId: dimensions.row,
        label: rowOption?.label || 'Row',
        type: this.resolveDimensionType(product, dimensions.row),
      });
    }
    if (dimensions.col) {
      const colOption = product.options?.find((o) => o.id === dimensions.col);
      dimensionsArray.push({
        optionId: dimensions.col,
        label: colOption?.label || 'Column',
        type: this.resolveDimensionType(product, dimensions.col),
      });
    }
    if (dimensions.group) {
      const groupOption = product.options?.find((o) => o.id === dimensions.group);
      dimensionsArray.push({
        optionId: dimensions.group,
        label: groupOption?.label || 'Group',
        type: this.resolveDimensionType(product, dimensions.group),
      });
    }

    // Convert grid to cells
    grid.forEach((row) => {
      const prices = Array.isArray(row?.prices)
        ? row.prices
        : Array.isArray(row?.values)
          ? row.values
          : [];
      const rowValue = row?.rowValue ?? row?.row_value;
      const groupValue = row?.groupValue ?? row?.group_value;

      prices.forEach((price, colIdx) => {
        if (colIdx >= columnValues.length) return;
        if (price > 0) {
          const cell = {
            dimensions: {
              [dimensions.row]: rowValue,
              [dimensions.col]: columnValues[colIdx],
            },
            price: price,
            available: true,
          };

          if (dimensions.group && groupValue !== undefined && groupValue !== null) {
            cell.dimensions[dimensions.group] = groupValue;
          }

          cells.push(cell);
        }
      });
    });

    const result = {
      ...pricingMatrix,
      cells,
      dimensions: dimensionsArray,
    };

    return result;
  }

  /**
   * Normalize a delta table to the matrix-like cells format.
   *
   * Supports:
   * - cells based format
   * - grid + dimensions + columnValues format
   */
  normalizeDeltaTable(table, product = {}) {
    if (!table || !table.dimensions) {
      return null;
    }

    const dimensions = [];
    if (table.dimensions.row) {
      const rowOption = product.options?.find((o) => o.id === table.dimensions.row);
      dimensions.push({
        optionId: table.dimensions.row,
        label: rowOption?.label || 'Rij',
        type: rowOption?.type === 'number' ? 'number' : 'choice',
      });
    }
    if (table.dimensions.col) {
      const colOption = product.options?.find((o) => o.id === table.dimensions.col);
      dimensions.push({
        optionId: table.dimensions.col,
        label: colOption?.label || 'Kolom',
        type: colOption?.type === 'number' ? 'number' : 'choice',
      });
    }
    if (table.dimensions.group) {
      const groupOption = product.options?.find((o) => o.id === table.dimensions.group);
      dimensions.push({
        optionId: table.dimensions.group,
        label: groupOption?.label || 'Groep',
        type: groupOption?.type === 'number' ? 'number' : 'choice',
      });
    }

    if (dimensions.length === 0) {
      return null;
    }

    // Already in cells format
    if (Array.isArray(table.cells) && table.cells.length > 0) {
      return {
        dimensions,
        cells: table.cells.map((cell) => ({
          dimensions: cell.dimensions || {},
          value: Number(cell.value ?? cell.price ?? 0) || 0,
          available: cell.available !== false,
        })),
      };
    }

    // Grid format
    const grid = Array.isArray(table.grid) ? table.grid : [];
    const columnValues = Array.isArray(table.columnValues)
      ? table.columnValues
      : Array.isArray(table.column_values)
        ? table.column_values
        : [];
    const hasCol = !!table.dimensions.col;
    const effectiveColumns = hasCol ? columnValues : [null];

    const cells = [];
    grid.forEach((row) => {
      const values = Array.isArray(row.values)
        ? row.values
        : Array.isArray(row.prices)
          ? row.prices
          : [];
      const rowValue = row?.rowValue ?? row?.row_value;
      const groupValue = row?.groupValue ?? row?.group_value;
      effectiveColumns.forEach((colValue, colIdx) => {
        const rawValue = values[colIdx];
        if (rawValue === undefined || rawValue === null || rawValue === '') {
          return;
        }

        const cell = {
          dimensions: {
            [table.dimensions.row]: rowValue,
          },
          value: Number(rawValue) || 0,
          available: true,
        };

        if (hasCol) {
          cell.dimensions[table.dimensions.col] = colValue;
        }
        if (table.dimensions.group && groupValue !== undefined && groupValue !== null) {
          cell.dimensions[table.dimensions.group] = groupValue;
        }

        cells.push(cell);
      });
    });

    return {
      dimensions,
      cells,
    };
  }
}

// Export singleton instance for convenience
export default new MatrixPricingEngine();
